import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LISTADOS, CeldaClaves, DatoCopiable, celdaTextoLargo, formatoFecha } from './definiciones';
import { CATEGORIAS, ESTADOS_ANSES, ORDEN_CATEGORIAS, ORIGEN_CONTROL, filtrarFilas, formatoExpediente, grupoDeFila, haceCuanto, ordenarFilas } from './paraAprobar';
import TablaListado from '../../components/Listados/TablaListado';
import AccesosListados from '../../components/Listados/AccesosListados';
import ModalExpedienteParaAprobar from '../../components/Listados/ModalExpedienteParaAprobar';
import ModalMisAvisos from '../../components/anses/ModalMisAvisos';
import ModalSeguirVarios from '../../components/anses/ModalSeguirVarios';
import RevisionManualSeguimiento from '../../components/Listados/RevisionManualSeguimiento';
import { rutaDetalleTramite } from '../../utils/rutasTramite';
import { getTableroParaAprobar, cambiarSincronizacionParaAprobar, seguirTramiteParaAprobar } from '../../services/tramiteService';
import { actualizarPrioridad } from '../../services/seguimientoExpedientesService';
import { apellidoNombre } from '../../utils/nombrePersona';
import { useAuth } from '../../auth/AuthContext';
import { ROLES } from '../../auth/roles';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';
import PageHeader from '../../components/ui/PageHeader';

const DEF = LISTADOS.paraAprobar;

const estiloEtiqueta = { fontWeight: 500, fontSize: '0.9rem', color: '#4b5563' };

const formatoFechaHora = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('es-AR');
};

/** Estado del trámite en el sistema + estado en ANSES (la pastilla de color que importa). */
function CeldaEstado({ row }) {
    const anses = ESTADOS_ANSES[row.estadoAnses];
    return (
        <div className="text-start" style={{ maxWidth: 180 }}>
            {anses && (
                <span
                    title={row.etapaAnses || ''}
                    style={{
                        display: 'inline-block', background: anses.bg, color: anses.fg, border: `1px solid ${anses.borde}`,
                        borderRadius: 12, padding: '1px 10px', fontSize: '0.75rem', fontWeight: 700, marginBottom: 2, whiteSpace: 'nowrap',
                    }}
                >
                    {anses.etiqueta}{row.fechaResolucionAnses && ` ${formatoFecha(row.fechaResolucionAnses)}`}
                </span>
            )}
            {row.etapaAnses && <div style={{ fontSize: '0.74rem', color: '#4b5563' }}><span style={{ fontSize: '0.74rem' }}>{celdaTextoLargo(row.etapaAnses, 175)}</span></div>}
            <div style={{ fontSize: '0.78rem', color: '#374151' }}>{row.estadoActual || '—'}</div>
            {row.fechaEstadoActual && (
                <div style={{ fontSize: '0.7rem', color: '#4b5563' }}>desde {formatoFechaHora(row.fechaEstadoActual)}</div>
            )}
        </div>
    );
}

/** Texto que se envuelve hasta 3 renglones (el resto queda en el title). */
function TextoEnvuelto({ valor, ancho }) {
    if (!valor) return <span style={{ color: '#6b7280' }}>—</span>;
    return (
        <span
            title={valor}
            style={{
                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                maxWidth: ancho, fontSize: '0.78rem', lineHeight: 1.3, color: '#1e293b', overflowWrap: 'break-word',
                textAlign: 'left',
            }}
        >
            {valor}
        </span>
    );
}

/** Cuándo se miró por última vez y si hace falta mirarlo a mano. */
function CeldaUltimoControl({ row }) {
    const fecha = formatoFechaHora(row.ultimoControl);
    const quien = [ORIGEN_CONTROL[row.tipoUltimoControl] || row.tipoUltimoControl,
        row.usuarioUltimoControl && row.usuarioUltimoControl.split('@')[0]].filter(Boolean).join(' · ');
    return (
        <div className="text-start" style={{ fontSize: '0.76rem', lineHeight: 1.3, maxWidth: 150 }}>
            {fecha ? (
                <>
                    <div style={{ color: '#1e293b', fontWeight: 600, fontSize: '0.76rem' }}>{fecha}</div>
                    <div style={{ color: '#4b5563', fontSize: '0.74rem' }}>{haceCuanto(row.ultimoControl)} · {quien}</div>
                </>
            ) : (
                <div style={{ color: '#4b5563', fontSize: '0.74rem' }}>Nunca controlado</div>
            )}
            {row.requiereControl && (
                <div style={{ color: '#c2410c', fontWeight: 600, fontSize: '0.74rem' }} title={row.motivoControl || ''}>
                    <i className="bi bi-exclamation-triangle-fill me-1" />Requiere control
                </div>
            )}
            {row.sincroniza && !row.requiereControl && (
                <div style={{ color: '#1d4ed8', fontSize: '0.72rem' }}><i className="bi bi-arrow-repeat me-1" />Automático</div>
            )}
        </div>
    );
}

/** Seguir el trámite: a quien lo sigue le llegan los avisos de ANSES de su expediente (Mis avisos). */
function BotonSeguir({ row, onCambiar }) {
    const [enviando, setEnviando] = useState(false);
    const alternar = async () => {
        setEnviando(true);
        try {
            await onCambiar(row, !row.seguido);
        } finally {
            setEnviando(false);
        }
    };
    return (
        <button
            type="button" className="btn btn-sm btn-link p-0" disabled={enviando}
            onClick={alternar}
            aria-pressed={Boolean(row.seguido)}
            title={row.seguido ? 'Lo seguís: te llegan sus avisos de ANSES. Clic para dejar de seguirlo.' : 'Seguir: te llegan sus avisos de ANSES'}
            style={{ fontSize: '0.76rem', color: row.seguido ? '#b45309' : '#4b5563', textDecoration: 'none' }}
        >
            <i className={`bi ${row.seguido ? 'bi-star-fill' : 'bi-star'} me-1`} style={{ color: row.seguido ? '#f59e0b' : undefined }} />
            {row.seguido ? 'Siguiendo' : 'Seguir'}
        </button>
    );
}

/** El detalle que replica las columnas de la planilla que no entran en la fila. */
/**
 * Cada cuánto revisa el robot a este cliente (ALTA 7 / MEDIA 14 / BAJA 21 días). El seguimiento ya no
 * se maneja por cliente sino desde Para aprobar (decisión del usuario, 24-oct-2026): se cambia acá.
 */
function SelectorPrioridad({ row, onCambiada }) {
    const [valor, setValor] = useState(row.prioridadSeguimiento || 'MEDIA');
    const [guardando, setGuardando] = useState(false);
    if (!row.prioridadSeguimiento) {
        return <span style={{ color: '#6b7280' }}>No está en el seguimiento del robot</span>;
    }
    const cambiar = async (e) => {
        const nueva = e.target.value;
        setGuardando(true);
        try {
            await actualizarPrioridad(row.cuilCliente, nueva);
            setValor(nueva);
            row.prioridadSeguimiento = nueva;
            toast.success('Prioridad del robot actualizada.');
            onCambiada?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo cambiar la prioridad.');
        } finally {
            setGuardando(false);
        }
    };
    return (
        <select className="form-select form-select-sm" style={{ width: 'auto' }} aria-label="Prioridad del robot"
                value={valor} disabled={guardando} onChange={cambiar} onClick={(e) => e.stopPropagation()}>
            <option value="ALTA">Alta · cada 7 días</option>
            <option value="MEDIA">Media · cada 14 días</option>
            <option value="BAJA">Baja · cada 21 días</option>
        </select>
    );
}

function DetalleFila({ row }) {
    const dato = (titulo, valor) => (
        <div>
            <div style={{ fontSize: '0.7rem', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{titulo}</div>
            <div style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap' }}>{valor || <span style={{ color: '#6b7280' }}>—</span>}</div>
        </div>
    );
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem 1.5rem' }}>
            {dato('Amparo por mora', row.amparoPorMora)}
            {dato('Pronto despacho', row.fechaProntoDespacho && formatoFecha(row.fechaProntoDespacho))}
            {dato('Estudios médicos', row.estudiosMedicos)}
            {dato('Estado de junta y pericia', row.estadoJuntaPericia)}
            {dato('Mi Argentina', row.miArgentina)}
            {dato('Último comentario', row.ultimoComentario)}
            {row.motivoControl && dato('Por qué requiere control', row.motivoControl)}
            {dato('Prioridad en el robot', <SelectorPrioridad row={row} />)}
            {/* Varios expedientes seguidos: arriba se ve el de la novedad (o el último que cambió); acá, todos. */}
            {row.expedientesAnses?.length > 1 && dato('Expedientes en ANSES', (
                <span style={{ display: 'block' }}>
                    {row.expedientesAnses.map((e) => (
                        <span key={e.nroExpediente} style={{ display: 'block' }}>
                            <span style={{ fontFamily: 'monospace' }}>{formatoExpediente(e.nroExpediente)}</span>
                            {' · '}{ESTADOS_ANSES[e.estado]?.etiqueta || e.estado}
                            {e.etapa && ` · ${e.etapa}`}
                        </span>
                    ))}
                </span>
            ))}
        </div>
    );
}

/**
 * "Para aprobar": los trámites presentados en ANSES que esperan resolución. Es la vista del
 * usuario (la del robot es Herramientas → Seguimiento de expedientes).
 *
 * Arriba, en secciones de color, lo que requiere una decisión: Favorables (verde), Desfavorables
 * (rojo), Citados (amarillo), actualizaciones del robot y los que nadie controló a tiempo. Debajo,
 * una sección por tipo de trámite en el orden de la planilla. Nada se mueve solo: un favorable
 * pasa a Favorables cuando un usuario lo confirma.
 *
 * Columnas pensadas para entrar sin scroll horizontal y parecerse a la planilla (referencia,
 * teléfono y claves siempre a la vista); el resto de las columnas de la planilla va en el detalle.
 */
export default function ListadoParaAprobar() {
    const navigate = useNavigate();
    const { esRol } = useAuth();
    const puedeConfigurar = esRol(ROLES.ADMIN, ROLES.PROGRAMADOR);
    const [meta, setMeta] = useState(null);
    const [recarga, setRecarga] = useState(0);
    const [modal, setModal] = useState(null);
    const [abiertos, setAbiertos] = useState(() => new Set());
    const [verConfiguracion, setVerConfiguracion] = useState(false);
    const [verMisAvisos, setVerMisAvisos] = useState(false);
    const [verSeguirVarios, setVerSeguirVarios] = useState(false);
    // Cambia al seguir/dejar de seguir: vuelve a pintar la tabla con lo que ya está en memoria,
    // sin pedir el tablero de nuevo (no está en la clave del cache, solo en las dependencias).
    const [version, setVersion] = useState(0);

    // Sección e ingreso a ANSES (como tenía el listado) más el estado en ANSES. No hay filtro
    // por tipo de trámite: hoy cada sección es un único tipo (Jubilaciones = JUBILACION...), así que
    // era el mismo filtro dos veces. Si hacen falta subtipos (24.476 / 27.705...), van acá. Persisten al volver de la ficha del trámite, como en el resto de los listados.
    const [seccion, setSeccion] = useFiltroPersistente('listadoParaAprobar.grupo', '');
    const [desde, setDesde] = useFiltroPersistente('listadoParaAprobar.desde', '');
    const [hasta, setHasta] = useFiltroPersistente('listadoParaAprobar.hasta', '');
    const [estadoAnses, setEstadoAnses] = useFiltroPersistente('listadoParaAprobar.estadoAnses', '');
    const [soloSeguidos, setSoloSeguidos] = useFiltroPersistente('listadoParaAprobar.soloSeguidos', false);

    // El tablero se pide una vez por búsqueda (y al guardar algo); los filtros se aplican sobre lo
    // que ya llegó, sin volver a consultar al robot de ANSES en cada cambio de filtro.
    const cache = useRef({ clave: null, tablero: null });
    const fetchPage = useCallback(async (_page, _size, texto) => {
        const clave = `${texto || ''}|${recarga}`;
        if (cache.current.clave !== clave) {
            cache.current = { clave, tablero: await getTableroParaAprobar(texto) };
        }
        const tablero = cache.current.tablero;
        setMeta(tablero);
        const filas = filtrarFilas(tablero?.filas || [], { seccion, desde, hasta, estadoAnses, soloSeguidos });
        return { content: ordenarFilas(filas, tablero?.secciones || []), totalPages: 0 };
        // version: re-pinta tras seguir/dejar de seguir (ver arriba).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recarga, seccion, desde, hasta, estadoAnses, soloSeguidos, version]);

    const cambiarSeguimiento = useCallback(async (row, seguir) => {
        try {
            await seguirTramiteParaAprobar(row.idTramite, seguir);
            const enCache = cache.current.tablero?.filas?.find((f) => f.idTramite === row.idTramite);
            if (enCache) enCache.seguido = seguir;
            setVersion((v) => v + 1);
        } catch {
            toast.error('No se pudo cambiar el seguimiento del trámite');
        }
    }, []);

    const alternarDetalle = useCallback((id) => {
        setAbiertos((prev) => {
            const siguiente = new Set(prev);
            if (siguiente.has(id)) siguiente.delete(id); else siguiente.add(id);
            return siguiente;
        });
    }, []);

    const columnas = useMemo(() => [
        {
            key: 'cliente', header: 'Cliente', className: 'text-start',
            render: (row) => (
                <div className="text-start" style={{ maxWidth: 175 }}>
                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.84rem', lineHeight: 1.25 }}>
                        {apellidoNombre(row.apellidoCliente, row.nombreCliente) || 'N/A'}
                    </div>
                    {row.cuilCliente && <DatoCopiable valor={row.cuilCliente} estilo={{ fontSize: '0.76rem', color: '#4b5563' }} />}
                </div>
            ),
        },
        {
            // Trámite y expediente juntos: es lo que se dicta junto ("la jubilación, expediente...").
            key: 'tramite', header: 'Trámite y expediente', className: 'text-start',
            render: (row) => (
                <div className="text-start" style={{ maxWidth: 175, fontSize: '0.78rem', lineHeight: 1.3 }}>
                    <div style={{ color: '#1e293b', fontWeight: 600, fontSize: '0.8rem' }}>{row.titulo || row.tipoTramite || '—'}</div>
                    {row.etiqueta && <div style={{ color: '#b45309', fontSize: '0.74rem' }}><span style={{ fontSize: '0.74rem' }}>{celdaTextoLargo(row.etiqueta, 175)}</span></div>}
                    {row.nroExpediente
                        ? <DatoCopiable valor={formatoExpediente(row.nroExpediente)} estilo={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#374151', overflowWrap: 'anywhere' }} />
                        : <span style={{ color: '#4b5563', fontSize: '0.74rem' }}>Sin N° de expediente</span>}
                    {row.fechaIngresoAnses && <div style={{ color: '#4b5563', fontSize: '0.74rem' }}>Ingresó {formatoFecha(row.fechaIngresoAnses)}</div>}
                </div>
            ),
        },
        // Referencia y teléfono se leen siempre: pasan a varios renglones en vez de cortarse.
        { key: 'referencia', header: 'Referencia', render: (row) => <TextoEnvuelto valor={row.referencia} ancho={120} /> },
        { key: 'telefono', header: 'Teléfono', render: (row) => <TextoEnvuelto valor={row.telefono} ancho={115} /> },
        { key: 'estado', header: 'Estado', className: 'text-start', render: (row) => <CeldaEstado row={row} /> },
        { key: 'control', header: 'Último control', className: 'text-start', render: (row) => <CeldaUltimoControl row={row} /> },
        { key: 'claves', header: 'Claves', className: 'text-start', render: (row) => <CeldaClaves row={row} /> },
        {
            key: 'acciones', header: '',
            render: (row) => (
                // stopPropagation: la fila entera navega a la ficha del trámite.
                // whiteSpace normal: la regla global td:has(> div > .btn) pone nowrap y "Pasar a Favorables"
                // estiraba la columna 29 px más allá de la tabla (scroll horizontal dentro del listado).
                <div className="d-flex flex-column gap-1 align-items-stretch" onClick={(e) => e.stopPropagation()}
                     style={{ width: 92, whiteSpace: 'normal', lineHeight: 1.15 }}>
                    {row.novedadPendiente && (
                        <button
                            type="button" className="btn btn-sm py-0"
                            style={{ background: CATEGORIAS[row.categoria]?.color, color: CATEGORIAS[row.categoria]?.colorTexto, fontWeight: 600, fontSize: '0.78rem' }}
                            onClick={() => setModal({ modo: 'confirmar', fila: row })}
                        >
                            {CATEGORIAS[row.categoria]?.accion || 'Resolver'}
                        </button>
                    )}
                    <button type="button" className="btn btn-sm btn-outline-primary py-0" style={{ fontSize: '0.78rem' }}
                        onClick={() => setModal({ modo: 'control', fila: row })}>
                        Controlar
                    </button>
                    <BotonSeguir row={row} onCambiar={cambiarSeguimiento} />
                    <button
                        type="button" className="btn btn-sm btn-link p-0" style={{ fontSize: '0.76rem' }}
                        onClick={() => alternarDetalle(row.idTramite)}
                        aria-expanded={abiertos.has(row.idTramite)}
                    >
                        <i className={`bi ${abiertos.has(row.idTramite) ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`} />Detalle
                    </button>
                </div>
            ),
        },
    ], [abiertos, alternarDetalle, cambiarSeguimiento]);

    const filaDetalle = useCallback((row) => (abiertos.has(row.idTramite) ? <DetalleFila row={row} /> : null), [abiertos]);

    const conteos = useMemo(() => {
        const c = {};
        (meta?.filas || []).forEach((f) => { if (f.categoria) c[f.categoria] = (c[f.categoria] || 0) + 1; });
        return c;
    }, [meta]);

    const etiquetaSeccion = (meta?.secciones || []).find((x) => x.ambito === seccion)?.etiqueta;
    const etiquetaEstado = ESTADOS_ANSES[estadoAnses]?.etiqueta
        || (estadoAnses === 'SIN_CONTROL' ? 'Nunca controlado' : 'Requiere control');
    const chipsExtra = [
        seccion && { key: 'seccion', label: etiquetaSeccion || 'Sección', onQuitar: () => setSeccion('') },
        estadoAnses && { key: 'estadoAnses', label: etiquetaEstado, onQuitar: () => setEstadoAnses('') },
        soloSeguidos && { key: 'soloSeguidos', label: 'Solo los que sigo', onQuitar: () => setSoloSeguidos(false) },
        (desde || hasta) && { key: 'fechas', label: `Ingreso ANSES: ${desde || '…'} a ${hasta || '…'}`, onQuitar: () => { setDesde(''); setHasta(''); } },
    ].filter(Boolean);

    const limpiarExtra = () => {
        setSeccion('');
        setEstadoAnses('');
        setSoloSeguidos(false);
        setDesde('');
        setHasta('');
    };

    const alGuardar = ({ modo, accion }) => {
        if (modo === 'confirmar' && accion === 'CARGAR_CITACION' && modal?.fila) {
            // La citación se carga desde la ficha del trámite (tarjeta del turno ANSES).
            navigate(rutaDetalleTramite(modal.fila));
            return;
        }
        setRecarga((n) => n + 1);
    };

    const cambiarSincronizacion = async (ambito, valor) => {
        try {
            await cambiarSincronizacionParaAprobar(ambito, valor);
            setRecarga((n) => n + 1);
        } catch {
            toast.error('No se pudo cambiar la sincronización');
        }
    };

    return (
        <div className="container-fluid mt-4 listado-pagina area-cobranzas">
            <PageHeader
                variante="plano"
                icono={DEF.icono}
                titulo={DEF.titulo}
                descripcion={DEF.subtitulo}
            />

            <AccesosListados actual={DEF.clave} />

            {meta && !meta.robotDisponible && (
                <div className="alert alert-warning py-2" role="status">
                    <i className="bi bi-exclamation-triangle me-2" />{meta.mensajeRobot}
                </div>
            )}

            {meta && (
                <div className="d-flex flex-wrap gap-2 mb-3" aria-label="Resumen de novedades">
                    {ORDEN_CATEGORIAS.filter((c) => conteos[c]).map((c) => (
                        <span key={c} style={{
                            background: CATEGORIAS[c].color, color: CATEGORIAS[c].colorTexto, borderRadius: 20,
                            padding: '4px 14px', fontWeight: 700, fontSize: '0.85rem',
                        }}>
                            {conteos[c]} · {CATEGORIAS[c].etiqueta.split(' — ')[0]}
                        </span>
                    ))}
                    {ORDEN_CATEGORIAS.every((c) => !conteos[c]) && (
                        <span className="text-muted" style={{ fontSize: '0.9rem' }}>Sin novedades para revisar.</span>
                    )}
                    <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => setVerSeguirVarios(true)}>
                        <i className="bi bi-star me-1" />Seguir varios
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setVerMisAvisos(true)}>
                        <i className="bi bi-bell me-1" />Mis avisos
                    </button>
                    {puedeConfigurar && (
                        <button type="button" className="btn btn-sm btn-link" onClick={() => setVerConfiguracion((v) => !v)}>
                            <i className="bi bi-gear me-1" />Seguimiento automático por sección
                        </button>
                    )}
                </div>
            )}

            {meta && <RevisionManualSeguimiento items={meta.revisionManual || []} />}

            {verConfiguracion && meta && (
                <div className="card mb-3" style={{ border: '1px solid #e5e7eb', borderRadius: 12 }}>
                    <div className="card-body py-2">
                        <div className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>
                            Las secciones encendidas suman de a poco sus clientes al robot de ANSES (un login en Mi ANSES por cliente)
                            y se controlan solas según la prioridad del seguimiento. Las apagadas se controlan a mano.
                        </div>
                        <div className="d-flex flex-wrap gap-3">
                            {meta.secciones.map((s) => (
                                <div className="form-check form-switch" key={s.ambito}>
                                    <input className="form-check-input" type="checkbox" id={`sync-${s.ambito}`}
                                        checked={s.sincroniza} onChange={(e) => cambiarSincronizacion(s.ambito, e.target.checked)} />
                                    <label className="form-check-label" htmlFor={`sync-${s.ambito}`} style={{ fontSize: '0.88rem' }}>{s.etiqueta}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <TablaListado
                claveFiltro="listadoParaAprobar"
                columnas={columnas}
                fetchPage={fetchPage}
                onRowClick={(row) => navigate(rutaDetalleTramite(row))}
                rowKey={(row) => row.idTramite}
                dependencias={[recarga, seccion, desde, hasta, estadoAnses, soloSeguidos, version]}
                agruparPor={grupoDeFila}
                filaDetalle={filaDetalle}
                compacto
                size={3000}
                chipsExtra={chipsExtra}
                onLimpiarExtra={limpiarExtra}
                filtrosExtra={
                    <>
                        <div>
                            <label className="form-label" style={estiloEtiqueta}><i className="bi bi-collection me-2"></i>Sección</label>
                            <select className="form-select" value={seccion} onChange={(e) => setSeccion(e.target.value)}>
                                <option value="">Todas</option>
                                {(meta?.secciones || []).map((x) => <option key={x.ambito} value={x.ambito}>{x.etiqueta}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="form-label" style={estiloEtiqueta}><i className="bi bi-flag me-2"></i>Estado en ANSES</label>
                            <select className="form-select" value={estadoAnses} onChange={(e) => setEstadoAnses(e.target.value)}>
                                <option value="">Todos</option>
                                {Object.entries(ESTADOS_ANSES).map(([v, e]) => <option key={v} value={v}>{e.etiqueta}</option>)}
                                <option value="SIN_CONTROL">Nunca controlado</option>
                                <option value="REQUIERE_CONTROL">Requiere control</option>
                            </select>
                        </div>
                        <div className="form-check mt-4">
                            <input className="form-check-input" type="checkbox" id="soloSeguidos"
                                checked={Boolean(soloSeguidos)} onChange={(e) => setSoloSeguidos(e.target.checked)} />
                            <label className="form-check-label" htmlFor="soloSeguidos" style={estiloEtiqueta}>
                                <i className="bi bi-star-fill me-1" style={{ color: '#f59e0b' }} />Solo los que sigo
                            </label>
                        </div>
                        <div>
                            <label className="form-label" style={estiloEtiqueta}><i className="bi bi-calendar-range me-2"></i>Ingreso a ANSES</label>
                            <div className="d-flex gap-2">
                                <input type="date" className="form-control" aria-label="Ingreso a ANSES desde" value={desde} onChange={(e) => setDesde(e.target.value)} />
                                <input type="date" className="form-control" aria-label="Ingreso a ANSES hasta" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} />
                            </div>
                        </div>
                    </>
                }
            />

            <ModalMisAvisos open={verMisAvisos} onClose={() => setVerMisAvisos(false)} />
            {verSeguirVarios && (
                <ModalSeguirVarios
                    open
                    onClose={() => setVerSeguirVarios(false)}
                    filas={cache.current.tablero?.filas || []}
                    secciones={meta?.secciones || []}
                    seccionInicial={seccion}
                    onHecho={({ ids, seguir }) => {
                        const marcar = new Set(ids);
                        (cache.current.tablero?.filas || []).forEach((f) => { if (marcar.has(f.idTramite)) f.seguido = seguir; });
                        setVersion((v) => v + 1);
                    }}
                />
            )}

            <ModalExpedienteParaAprobar
                open={Boolean(modal)}
                modo={modal?.modo}
                fila={modal?.fila}
                onClose={() => setModal(null)}
                onGuardado={alGuardar}
            />
        </div>
    );
}
